import React, { useEffect, useMemo, useState } from 'react';
import { Edit2, Leaf, Save, Trash2 } from 'lucide-react';

function emptyCropForm() {
  return {
    id: null,
    seed_item_id: '',
    harvest_item_id: '',
    harvest_quantity: 1,
    growth_seconds: 420,
    stage_count: 7,
    metadata: '{}',
    is_active: true
  };
}

function parseJsonText(value) {
  if (!value) return {};
  try {
    return JSON.parse(value);
  } catch (_) {
    return {};
  }
}

function normalizeCropForm(form) {
  return {
    id: form.id || null,
    seed_item_id: Number(form.seed_item_id) || 0,
    harvest_item_id: Number(form.harvest_item_id) || 0,
    harvest_quantity: Math.max(1, Number(form.harvest_quantity) || 1),
    growth_seconds: Math.max(1, Number(form.growth_seconds) || 420),
    stage_count: Math.max(1, Number(form.stage_count) || 7),
    metadata: parseJsonText(form.metadata),
    is_active: form.is_active === false ? 0 : 1
  };
}

function buildFormFromRule(rule) {
  return {
    id: rule.id,
    seed_item_id: String(rule.seed_item_id || ''),
    harvest_item_id: String(rule.harvest_item_id || ''),
    harvest_quantity: Number(rule.harvest_quantity) || 1,
    growth_seconds: Number(rule.growth_seconds) || 420,
    stage_count: Number(rule.stage_count) || 7,
    metadata: JSON.stringify(rule.metadata || {}, null, 2),
    is_active: !!rule.is_active
  };
}

function resolveImageSrc(image) {
  if (!image) return '';
  if (/^https?:\/\//i.test(image) || image.startsWith('/')) return image;
  const apiBaseUrl = import.meta.env.VITE_API_URL || '';
  return `${apiBaseUrl}/uploads/gallery/${image}`;
}

function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds) || 0);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes <= 0) return `${remainingSeconds}s`;
  return `${minutes}m ${String(remainingSeconds).padStart(2, '0')}s`;
}

export default function RpgCropManager({
  transformations = [],
  items = [],
  onSaveTransformation,
  onDeleteTransformation,
  isSaving = false
}) {
  const [cropForm, setCropForm] = useState(emptyCropForm());

  const normalizedItems = useMemo(() => {
    if (Array.isArray(items)) return items;
    if (Array.isArray(items?.items)) return items.items;
    if (Array.isArray(items?.data)) return items.data;
    return [];
  }, [items]);

  const seedItems = useMemo(
    () => normalizedItems.filter((item) => [13, 14].includes(Number(item.item_type))),
    [normalizedItems]
  );
  const harvestItems = useMemo(
    () => normalizedItems.filter((item) => Number(item.item_type) === 13),
    [normalizedItems]
  );
  const sortedRules = useMemo(
    () => [...transformations].sort((a, b) => String(a?.seed_item?.name || '').localeCompare(String(b?.seed_item?.name || ''))),
    [transformations]
  );

  useEffect(() => {
    if (!cropForm.seed_item_id && seedItems.length) {
      setCropForm((current) => ({ ...current, seed_item_id: String(seedItems[0].id) }));
    }
  }, [cropForm.seed_item_id, seedItems]);

  useEffect(() => {
    if (!cropForm.harvest_item_id && harvestItems.length) {
      setCropForm((current) => ({ ...current, harvest_item_id: String(harvestItems[0].id) }));
    }
  }, [cropForm.harvest_item_id, harvestItems]);

  const selectedSeed = seedItems.find((item) => Number(item.id) === Number(cropForm.seed_item_id));
  const selectedHarvest = harvestItems.find((item) => Number(item.id) === Number(cropForm.harvest_item_id));

  const submitForm = (e) => {
    e.preventDefault();
    onSaveTransformation(normalizeCropForm(cropForm));
    if (!cropForm.id) {
      setCropForm(emptyCropForm());
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-6">
      <section className="bg-[#11132d] rounded-3xl border border-slate-800 shadow-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-900/30">
          <div>
            <h3 className="font-bold text-white">Reglas de cultivo</h3>
            <p className="text-xs text-slate-500">Define que semilla se transforma en que producto final y cuanto tarda.</p>
          </div>
        </div>
        <div className="divide-y divide-slate-800/70">
          {sortedRules.length === 0 ? (
            <div className="p-8 text-sm text-slate-500">Todavia no hay reglas de cultivo creadas.</div>
          ) : sortedRules.map((rule) => (
            <div key={rule.id} className="p-4 flex items-center justify-between gap-4 hover:bg-white/5 transition-colors">
              <div className="flex items-center gap-4 min-w-0">
                <div className="w-14 h-14 rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden flex items-center justify-center shrink-0">
                  {rule.seed_item?.image ? (
                    <img src={resolveImageSrc(rule.seed_item.image)} alt={rule.seed_item.name} className="w-full h-full object-cover" />
                  ) : (
                    <Leaf size={18} className="text-slate-500" />
                  )}
                </div>
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="font-bold text-white truncate">{rule.seed_item?.name || 'Semilla desconocida'}</h4>
                    <span className="rounded-full border border-slate-700 bg-slate-900 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-slate-300">
                      {rule.is_active ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-300 truncate">
                    Produce {rule.harvest_item?.name || 'producto desconocido'} x{rule.harvest_quantity}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    Crecimiento: {formatDuration(rule.growth_seconds)} · Fases: {rule.stage_count}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCropForm(buildFormFromRule(rule))}
                  className="rounded-lg bg-slate-800 p-2 text-slate-400 hover:text-white"
                >
                  <Edit2 size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => onDeleteTransformation(rule.id)}
                  className="rounded-lg bg-slate-800 p-2 text-slate-400 hover:text-rose-400"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[#11132d] rounded-3xl border border-slate-800 shadow-xl p-6 space-y-5">
        <div className="flex items-center gap-3 border-b border-slate-800 pb-4">
          <Leaf size={22} className="text-emerald-400" />
          <div>
            <h3 className="font-bold text-white">{cropForm.id ? 'Editar transformacion' : 'Nueva transformacion'}</h3>
            <p className="text-xs text-slate-500">Une una semilla con el cultivo final que recibira el jugador al cosechar.</p>
          </div>
        </div>

        <form onSubmit={submitForm} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Semilla origen</label>
              <select
                value={cropForm.seed_item_id}
                onChange={(e) => setCropForm((current) => ({ ...current, seed_item_id: e.target.value }))}
                className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
              >
                <option value="">Selecciona una semilla</option>
                {seedItems.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Producto final</label>
              <select
                value={cropForm.harvest_item_id}
                onChange={(e) => setCropForm((current) => ({ ...current, harvest_item_id: e.target.value }))}
                className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
              >
                <option value="">Selecciona el cultivo final</option>
                {harvestItems.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Cantidad cosechada</label>
              <input
                type="number"
                min="1"
                value={cropForm.harvest_quantity}
                onChange={(e) => setCropForm((current) => ({ ...current, harvest_quantity: e.target.value }))}
                className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Tiempo de crecimiento (s)</label>
              <input
                type="number"
                min="1"
                value={cropForm.growth_seconds}
                onChange={(e) => setCropForm((current) => ({ ...current, growth_seconds: e.target.value }))}
                className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Fases visuales</label>
              <input
                type="number"
                min="1"
                value={cropForm.stage_count}
                onChange={(e) => setCropForm((current) => ({ ...current, stage_count: e.target.value }))}
                className="w-full rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none"
              />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-800 bg-slate-950/40 p-4 text-sm text-slate-400 space-y-2">
            <div className="font-bold text-white">Vista previa de la regla</div>
            <p>
              {selectedSeed?.name || 'Semilla'} se transforma en {selectedHarvest?.name || 'cultivo final'}
              {' '}x{Math.max(1, Number(cropForm.harvest_quantity) || 1)} en {formatDuration(cropForm.growth_seconds)}.
            </p>
            <p>
              La planta recorrera {Math.max(1, Number(cropForm.stage_count) || 1)} fases visuales antes de poder cosecharse.
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-400">Metadata JSON</label>
            <textarea
              rows={5}
              value={cropForm.metadata}
              onChange={(e) => setCropForm((current) => ({ ...current, metadata: e.target.value }))}
              className="w-full rounded-2xl border border-slate-700 bg-slate-900/50 p-3 text-white focus:border-game-primary focus:outline-none font-mono text-sm"
            />
          </div>

          <label className="flex items-center justify-between gap-3 rounded-2xl border border-slate-800 bg-slate-950/40 p-4 text-sm text-slate-300">
            <span>Regla activa</span>
            <input
              type="checkbox"
              checked={cropForm.is_active}
              onChange={(e) => setCropForm((current) => ({ ...current, is_active: e.target.checked }))}
              className="h-4 w-4 accent-game-primary"
            />
          </label>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-game-primary px-4 py-3 font-bold text-white hover:bg-game-primary/80 disabled:opacity-50"
            >
              <Save size={16} /> {isSaving ? 'Guardando...' : cropForm.id ? 'Guardar regla' : 'Crear regla'}
            </button>
            <button
              type="button"
              onClick={() => setCropForm(emptyCropForm())}
              className="rounded-xl border border-slate-700 bg-slate-900/50 px-4 py-3 font-bold text-slate-300 hover:text-white"
            >
              Limpiar
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
